import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AssetAmountField } from "@/components/AssetAmountField";
import { useGummiwormContext } from "@/context/GummiwormContext";
import { useTable } from "spacetimedb/react";
import { tables } from "@module-bindings";
import type { Market } from "@module-bindings/types";
import {
  formatAssetLabel,
  formatMarketPairLabel,
  formatAssetValue,
  findAssetBalance,
  assetIdKey,
  assetIdToApi,
  collectTokens,
  findMarketForPair,
  getAssetDecimals,
  pairableTokenKeys,
  type SwapToken,
} from "@/lib/marketUtils";
import { useHandlePlaceOrderRequest } from "@/hooks/useHandlePlaceOrderRequest";
import { useNextNonce } from "@/hooks/useNextNonce";
import { toast } from "sonner";

import type { Order } from "@module-bindings/types";

type OrderType = "market" | "limit";

/** Convert a u128 fixed-point price (60 fractional bits) to a decimal string. */
function fixedPointToDecimal(raw: bigint): string {
  const integer = raw >> FRAC_BITS;
  const fractional = raw & ((1n << FRAC_BITS) - 1n);
  if (fractional === 0n) return integer.toString();
  const scaled = (fractional * 1_000_000_000_000_000_000n + (1n << FRAC_BITS) / 2n) >> FRAC_BITS;
  const fracStr = scaled.toString().padStart(18, "0").replace(/0+$/, "").slice(0, 6);
  return fracStr ? `${integer}.${fracStr}` : integer.toString();
}

const SLIPPAGE_BUFFER_PCT = 2; // 2% beyond worst fill price
const FRAC_BITS = 60n;

/**
 * Walk the opposing order book to find the worst price needed to fill `quantity`,
 * then add a slippage buffer. Returns a decimal price string for the Hydrozoa API.
 */
function computeMarketPrice(
  quantity: bigint,
  side: "Bid" | "Ask",
  opposingOrders: readonly Order[],
): string {
  let remaining = quantity;
  let worstPrice = 0n;

  for (const order of opposingOrders) {
    if (remaining <= 0n) break;
    worstPrice = order.price;
    remaining -= order.remaining < remaining ? order.remaining : remaining;
  }

  if (worstPrice === 0n) {
    // No orders at all — use extreme fallback
    return side === "Bid" ? "999999999" : "0.000000000000000001";
  }

  // Apply slippage buffer: Bids go higher, Asks go lower
  const buffer = worstPrice * BigInt(SLIPPAGE_BUFFER_PCT) / 100n;
  const limitPrice = side === "Bid"
    ? worstPrice + buffer
    : worstPrice > buffer ? worstPrice - buffer : 1n;

  // Convert fixed-point u128 (60 fractional bits) to decimal string
  const integer = limitPrice >> FRAC_BITS;
  const fractional = limitPrice & ((1n << FRAC_BITS) - 1n);
  if (fractional === 0n) return integer.toString();
  const scaled = (fractional * 1_000_000_000_000_000_000n + (1n << FRAC_BITS) / 2n) >> FRAC_BITS;
  const fracStr = scaled.toString().padStart(18, "0").replace(/0+$/, "");
  return `${integer}.${fracStr}`;
}

const NONE_TOKEN = { assetId: "__none__", name: "Select" };

interface SwapPanelProps {
  selectedMarket: Market | null;
  markets: readonly Market[];
  onSelectMarket: (market: Market | null) => void;
  sellKey: string | null;
  buyKey: string | null;
  onSellKeyChange: (key: string | null) => void;
  onBuyKeyChange: (key: string | null) => void;
  orderType: OrderType;
  onOrderTypeChange: (type: OrderType) => void;
  limitPrice: string;
  onLimitPriceChange: (price: string) => void;
}

export const SwapPanel: FC<SwapPanelProps> = ({
  selectedMarket, markets, onSelectMarket,
  sellKey, buyKey, onSellKeyChange: setSellKey, onBuyKeyChange: setBuyKey,
  orderType, onOrderTypeChange: setOrderType,
  limitPrice, onLimitPriceChange: setLimitPrice,
}) => {
  const [swapAmount, setSwapAmount] = useState("");
  const [sliderValue, setSliderValue] = useState(0);
  const { accounts, myAccountId } = useGummiwormContext();
  const { mutateAsync: placeOrder, isPending: isPlacingOrder } = useHandlePlaceOrderRequest();
  const [allOrders] = useTable(tables.orders);
  const nextNonce = useNextNonce();

  const myAccount = useMemo(
    () => accounts.find((a) => a.accountId === myAccountId),
    [accounts, myAccountId],
  );

  const allTokens = useMemo(() => collectTokens(markets), [markets]);
  const tokensByKey = useMemo(
    () => new Map(allTokens.map((t) => [t.key, t])),
    [allTokens],
  );

  // Resolve matched market from current token selections
  const matched = useMemo(
    () => (sellKey && buyKey ? findMarketForPair(sellKey, buyKey, markets) : null),
    [sellKey, buyKey, markets],
  );

  const handleSelectSell = useCallback(
    (assetId: string) => {
      const token = allTokens.find((t) => t.key === assetId);
      if (!token) return;
      setSellKey(token.key);
      // Check if current buy + new sell form a valid market
      if (buyKey) {
        const pair = findMarketForPair(token.key, buyKey, markets);
        if (!pair) setBuyKey(null); // no market → clear other side
      }
    },
    [allTokens, buyKey, markets],
  );

  const handleSelectBuy = useCallback(
    (assetId: string) => {
      const token = allTokens.find((t) => t.key === assetId);
      if (!token) return;
      setBuyKey(token.key);
      // Check if current sell + new buy form a valid market
      if (sellKey) {
        const pair = findMarketForPair(sellKey, token.key, markets);
        if (!pair) setSellKey(null); // no market → clear other side
      }
    },
    [allTokens, sellKey, markets],
  );

  const handleFlip = useCallback(() => {
    setSellKey(buyKey);
    setBuyKey(sellKey);
  }, [sellKey, buyKey]);

  const sellToken = sellKey ? tokensByKey.get(sellKey) : null;
  const buyToken = buyKey ? tokensByKey.get(buyKey) : null;

  // Filter each token list to only tokens that can pair with the other side's selection
  const sellTokenList = useMemo(() => {
    const pairable = buyKey ? pairableTokenKeys(buyKey, markets) : null;
    return allTokens
      .filter((t) => !pairable || pairable.has(t.key))
      .map((t) => ({ assetId: t.key, name: t.label }));
  }, [allTokens, buyKey, markets]);

  const buyTokenList = useMemo(() => {
    const pairable = sellKey ? pairableTokenKeys(sellKey, markets) : null;
    return allTokens
      .filter((t) => !pairable || pairable.has(t.key))
      .map((t) => ({ assetId: t.key, name: t.label }));
  }, [allTokens, sellKey, markets]);

  const sellUsed = useMemo(() => new Set(buyKey ? [buyKey] : []), [buyKey]);
  const buyUsed = useMemo(() => new Set(sellKey ? [sellKey] : []), [sellKey]);

  // L2 balance for the sell-side token
  const sellBalance = useMemo(() => {
    if (!myAccount || !sellToken) return null;
    const raw = findAssetBalance(myAccount.uncommittedValue.assets, sellToken.key);
    return { raw, formatted: formatAssetValue(sellToken.asset, raw) };
  }, [myAccount, sellToken]);

  // Build sorted opposing order book for the matched market.
  // Orders on the opposite side, sorted by best price first.
  // Price is u128 fixed-point (60 fractional bits).
  const opposingOrders = useMemo(() => {
    if (!matched) return [];
    const { market, inverted } = matched;
    const baseKey = assetIdKey(market.tradingPair.base);
    const quoteKey = assetIdKey(market.tradingPair.quote);
    // If we're selling base (Ask), we match against Bids (sorted high→low).
    // If we're buying base (Bid/inverted), we match against Asks (sorted low→high).
    const opposingSide = inverted ? "Ask" : "Bid";
    const orders = allOrders.filter((o) => {
      if (o.remaining === 0n) return false;
      const ob = assetIdKey(o.pair.base);
      const oq = assetIdKey(o.pair.quote);
      return ob === baseKey && oq === quoteKey && o.side.tag === opposingSide;
    });
    // Best price first: Bids high→low, Asks low→high
    orders.sort((a, b) =>
      opposingSide === "Bid"
        ? (b.price > a.price ? 1 : b.price < a.price ? -1 : 0)
        : (a.price > b.price ? 1 : a.price < b.price ? -1 : 0)
    );
    return orders;
  }, [matched, allOrders]);

  // Total opposing liquidity (in base units)
  const opposingLiquidity = useMemo(() => {
    let total = 0n;
    for (const o of opposingOrders) total += o.remaining;
    return total;
  }, [opposingOrders]);

  const hasLiquidity = opposingLiquidity > 0n;

  // If market order mode and no liquidity, force limit
  const effectiveOrderType = orderType === "market" && !hasLiquidity ? "limit" : orderType;

  // For market orders, compute the effective max in raw sell-side units
  const marketMaxRaw = useMemo(() => {
    if (!matched || opposingLiquidity <= 0n) return null;
    if (!matched.inverted) {
      // Selling base: max = opposing liquidity in base
      return opposingLiquidity;
    } else {
      // Selling quote to buy base: max quote = sum(order.remaining * price >> 60)
      let total = 0n;
      for (const order of opposingOrders) {
        total += (order.remaining * order.price) >> FRAC_BITS;
      }
      return total;
    }
  }, [matched, opposingLiquidity, opposingOrders]);

  // The effective max the slider/input can reach: min(balance, market liquidity)
  const effectiveMaxRaw = useMemo(() => {
    if (!sellBalance || sellBalance.raw <= 0n) return 0n;
    if (effectiveOrderType === "market" && marketMaxRaw !== null && marketMaxRaw < sellBalance.raw) {
      return marketMaxRaw;
    }
    return sellBalance.raw;
  }, [sellBalance, effectiveOrderType, marketMaxRaw]);

  const setAmountFromPercent = useCallback(
    (pct: number) => {
      setSliderValue(pct);
      if (!sellToken || effectiveMaxRaw <= 0n || pct === 0) {
        setSwapAmount("");
        return;
      }
      const raw = effectiveMaxRaw * BigInt(pct) / 100n;
      setSwapAmount(formatAssetValue(sellToken.asset, raw));
    },
    [sellToken, effectiveMaxRaw],
  );

  // Keep slider and amount in sync during drag without recreating the handler
  const effectiveMaxRef = useRef(effectiveMaxRaw);
  const sellTokenRef = useRef(sellToken);
  effectiveMaxRef.current = effectiveMaxRaw;
  sellTokenRef.current = sellToken;

  const handleSliderChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const pct = Number(e.target.value);
      setSliderValue(pct);
      const max = effectiveMaxRef.current;
      const tok = sellTokenRef.current;
      if (!tok || max <= 0n || pct === 0) {
        setSwapAmount("");
        return;
      }
      const raw = max * BigInt(pct) / 100n;
      setSwapAmount(formatAssetValue(tok.asset, raw));
    },
    [],
  );

  const handlePlaceOrder = useCallback(async () => {
    if (!matched || !sellToken || !myAccount || !swapAmount) return;

    const { market, inverted } = matched;
    // Determine side: selling base = Ask, buying base = Bid
    const side: "Bid" | "Ask" = inverted ? "Bid" : "Ask";

    // Convert human-readable amount to raw quantity
    const decimals = getAssetDecimals(sellToken.asset);
    const cleanAmount = swapAmount.replace(/,/g, "");
    const parts = cleanAmount.split(".");
    const wholePart = BigInt(parts[0] || "0");
    const fracStr = (parts[1] || "").padEnd(decimals, "0").slice(0, decimals);
    const fracPart = fracStr ? BigInt(fracStr) : 0n;
    const rawQuantity = wholePart * 10n ** BigInt(decimals) + fracPart;

    if (rawQuantity <= 0n) return;

    // Determine price
    let price: string;
    if (effectiveOrderType === "limit") {
      const trimmed = limitPrice.trim();
      if (!trimmed || !/^\d*\.?\d{0,6}$/.test(trimmed) || trimmed === "." || trimmed === "") {
        toast.error("Enter a valid price (up to 6 decimal places)");
        return;
      }
      // Normalize ".28" → "0.28"
      price = trimmed.startsWith(".") ? "0" + trimmed : trimmed;
    } else {
      // Market order: walk the book to find the worst fill price, add slippage buffer
      price = computeMarketPrice(rawQuantity, side, opposingOrders);
    }

    try {
      await placeOrder({
        tradingPair: {
          base: assetIdToApi(market.tradingPair.base),
          quote: assetIdToApi(market.tradingPair.quote),
        },
        side,
        price,
        quantity: Number(rawQuantity),
        destination: null,
        nonce: nextNonce()!,
      });

      toast.success("Order placed!");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to place order");
    }
  }, [matched, sellToken, myAccount, swapAmount, limitPrice, effectiveOrderType, placeOrder, opposingOrders]);

  const numericAmount = parseFloat(swapAmount.replace(/,/g, "")) || 0;
  const numericPrice = parseFloat(limitPrice) || 0;

  // Convert the sell amount to raw base units for order book matching
  const sellDecimals = sellToken ? getAssetDecimals(sellToken.asset) : 6;

  // Estimate receive amount and average fill price by walking the order book (market)
  // or using the entered price (limit).
  // Price is quote-per-base (fixed-point u128, 60 fractional bits).
  const { receiveAmount, avgFillPrice } = useMemo(() => {
    const empty = { receiveAmount: "", avgFillPrice: "" };
    if (numericAmount <= 0 || !matched) return empty;

    if (effectiveOrderType === "limit" && numericPrice > 0) {
      const result = matched.inverted
        ? numericAmount / numericPrice
        : numericAmount * numericPrice;
      return { receiveAmount: result > 0 ? result.toFixed(6) : "", avgFillPrice: "" };
    }

    if (effectiveOrderType !== "market" || opposingOrders.length === 0) return empty;

    const rawSellAmount = BigInt(Math.round(numericAmount * 10 ** sellDecimals));
    let remaining = rawSellAmount;
    let receiveRaw = 0n;
    let baseFilled = 0n;
    let quoteFilled = 0n;
    const hitPrices = new Set<bigint>();

    if (!matched.inverted) {
      // Selling base (Ask): match against Bids
      for (const order of opposingOrders) {
        if (remaining <= 0n) break;
        const fill = remaining < order.remaining ? remaining : order.remaining;
        const quoteReceived = (fill * order.price) >> FRAC_BITS;
        receiveRaw += quoteReceived;
        baseFilled += fill;
        quoteFilled += quoteReceived;
        hitPrices.add(order.price);
        remaining -= fill;
      }
    } else {
      // Buying base (Bid/inverted): selling quote to get base
      for (const order of opposingOrders) {
        if (remaining <= 0n) break;
        const costPerUnit = order.price;
        const baseCanBuy = (remaining << FRAC_BITS) / costPerUnit;
        const fill = baseCanBuy < order.remaining ? baseCanBuy : order.remaining;
        const quoteCost = (fill * costPerUnit) >> FRAC_BITS;
        receiveRaw += fill;
        baseFilled += fill;
        quoteFilled += quoteCost;
        hitPrices.add(order.price);
        remaining -= quoteCost;
      }
    }

    if (receiveRaw <= 0n) return empty;
    const buyAsset = matched.inverted
      ? matched.market.tradingPair.base
      : matched.market.tradingPair.quote;
    const receiveStr = formatAssetValue(buyAsset, receiveRaw);

    // Average fill price
    let avgStr = "";
    if (hitPrices.size === 1) {
      // Single price level — convert fixed-point to decimal exactly
      const p = hitPrices.values().next().value!;
      avgStr = fixedPointToDecimal(p);
    } else if (baseFilled > 0n && quoteFilled > 0n) {
      // Weighted average in bigint with 18-digit precision
      const PRECISION = 1_000_000_000_000_000_000n;
      const s = (quoteFilled * PRECISION) / baseFilled;
      const w = s / PRECISION;
      const f = s % PRECISION;
      const fs = f.toString().padStart(18, "0").replace(/0+$/, "").slice(0, 6);
      avgStr = fs ? `${w}.${fs}` : w.toString();
    }

    return { receiveAmount: receiveStr, avgFillPrice: avgStr };
  }, [numericAmount, numericPrice, effectiveOrderType, matched, opposingOrders, sellDecimals]);


  const maxMarketDisplay = useMemo(() => {
    if (!sellToken || marketMaxRaw === null || marketMaxRaw <= 0n) return null;
    return formatAssetValue(sellToken.asset, marketMaxRaw);
  }, [sellToken, marketMaxRaw]);


  const sellDisplay = sellToken ? { assetId: sellToken.key, name: sellToken.label } : NONE_TOKEN;
  const buyDisplay = buyToken ? { assetId: buyToken.key, name: buyToken.label } : NONE_TOKEN;

  // Determine if we're selling or buying the base asset
  const isSellingSide = !matched?.inverted; // selling base = Ask
  const baseLabel = matched ? formatAssetLabel(matched.market.tradingPair.base) : null;

  return (
    <div className="flex flex-col gap-3 rounded-xl bg-[#23141A] px-4 py-4">
      {/* Market / Limit toggle */}
      <div className="flex border-b border-sr-border-subtle/70">
        {(["market", "limit"] as const).map((type) => {
          const isSelected = orderType === type;
          const isDisabled = type === "market" && matched != null && !hasLiquidity;
          return (
            <button
              key={type}
              onClick={() => setOrderType(type)}
              disabled={isDisabled}
              className={`border-b-2 px-4 py-2 text-sm font-medium capitalize transition-colors ${
                isSelected && !isDisabled
                  ? "border-sr-lemon-muted text-sr-lemon-muted"
                  : isDisabled
                    ? "border-transparent text-sr-neutral-700 cursor-not-allowed"
                    : "border-transparent text-sr-neutral-500 hover:text-sr-neutral-300"
              }`}
            >
              {type}
            </button>
          );
        })}
        {matched && !hasLiquidity && (
          <span className="ml-auto self-center text-[10px] text-sr-tangerine">
            No liquidity
          </span>
        )}
      </div>

      <div className="mt-1 flex flex-col gap-2">
        {/* Limit price input */}
        {effectiveOrderType === "limit" && (
          <div className="flex items-center gap-2 rounded-[20px] bg-[#170A10] px-4 py-3">
            <span className="text-xs text-sr-neutral-500 shrink-0">Price</span>
            <input
              type="text"
              inputMode="decimal"
              placeholder="0.00"
              value={limitPrice}
              onChange={(e) => {
                const v = e.target.value;
                // Allow digits, one dot, up to 6 decimal places
                if (v === "" || /^\d*\.?\d{0,6}$/.test(v)) {
                  setLimitPrice(v);
                }
              }}
              className="flex-1 bg-transparent text-right text-lg font-medium text-white tabular-nums outline-none placeholder:text-sr-neutral-700 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
            />
            <span className="text-xs text-sr-neutral-500 shrink-0">
              {matched ? `${formatAssetLabel(matched.market.tradingPair.quote)}/${formatAssetLabel(matched.market.tradingPair.base)}` : "—"}
            </span>
          </div>
        )}

        <div className="flex flex-col gap-0.5">
          <div className="relative z-10">
            <AssetAmountField
              amount={swapAmount}
              label={baseLabel ? (isSellingSide ? `Sell ${baseLabel}` : `Spend`) : "You swap"}
              onAmountChange={(v) => {
                if (sellToken && v && effectiveMaxRaw > 0n) {
                  const num = parseFloat(v.replace(/,/g, ""));
                  if (!isNaN(num) && num > 0) {
                    const decimals = getAssetDecimals(sellToken.asset);
                    const raw = BigInt(Math.round(num * 10 ** decimals));
                    if (raw > effectiveMaxRaw) {
                      setSwapAmount(formatAssetValue(sellToken.asset, effectiveMaxRaw));
                      return;
                    }
                  }
                }
                setSwapAmount(v);
              }}
              token={sellDisplay}
              tokens={sellTokenList}
              usedAssetIds={sellUsed}
              onSelectToken={handleSelectSell}
              helperText={undefined}
              footerText={sellToken && (
                <>
                  <span>Available balance:</span>
                  <br />
                  <span className="text-sr-bubblegum">
                    {sellBalance ? `${sellBalance.formatted} ${sellToken.label}` : "—"}
                  </span>
                  {effectiveOrderType === "market" && maxMarketDisplay && (
                    <>
                      <br />
                      <span className="text-sr-neutral-500">
                        Max market order: {maxMarketDisplay} {sellToken.label}
                      </span>
                    </>
                  )}
                </>
              )}
              rightTopContent={
                <div className="flex gap-1.5">
                  {([["25%", 25], ["50%", 50], ["MAX", 100]] as const).map(([label, pct]) => (
                    <button
                      key={label}
                      onClick={() => setAmountFromPercent(pct)}
                      className="rounded-full bg-sr-lemon-muted/10 px-2 py-0.5 text-[10px] font-medium text-sr-lemon-muted transition-colors hover:bg-sr-lemon-muted/20"
                    >
                      {label}
                    </button>
                  ))}
                </div>
              }
              amountClassName="text-[26px]"
              className="rounded-[28px] bg-[#170A10]"
              bottomContent={
                <div className="relative px-1">
                  <div className="relative flex h-6 items-center">
                    <div className="absolute inset-x-0 h-[2px] rounded-full bg-sr-neutral-700" />
                    <div
                      className="absolute h-[2px] rounded-full bg-sr-lemon-muted"
                      style={{ width: `${sliderValue}%` }}
                    />
                    <div className="absolute inset-x-0 flex justify-between pointer-events-none">
                      {[0, 25, 50, 75, 100].map((v) => (
                        <div
                          key={v}
                          className={`h-2 w-2 rounded-full border-2 transition-colors ${
                            sliderValue >= v
                              ? "border-sr-lemon-muted bg-sr-lemon-muted"
                              : "border-sr-neutral-700 bg-sr-espresso"
                          }`}
                        />
                      ))}
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={sliderValue}
                      onChange={handleSliderChange}
                      className="absolute inset-0 w-full cursor-pointer opacity-0"
                    />
                    <div
                      className="absolute h-4 w-4 rounded-full border-[3px] border-sr-espresso bg-sr-lemon-muted shadow-[0_0_0_1px_var(--color-sr-lemon-muted)] pointer-events-none"
                      style={{
                        left: `calc(${sliderValue}% - 8px)`,
                      }}
                    />
                  </div>
                </div>
              }
            />
          </div>

          <div className="relative z-20 flex justify-center">
            <div className="pointer-events-none absolute top-1/2 z-0 h-2 w-20 -translate-y-1/2 bg-[#23141A]" />
            <button
              onClick={handleFlip}
              disabled={!sellKey && !buyKey}
              className="relative z-10 -my-3.5 flex h-8 min-w-[72px] items-center justify-center rounded-full bg-[#22141B] px-3 text-sr-neutral-300 transition-colors hover:text-sr-lemon-muted disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M4.5 2.5V13.5M4.5 13.5L2 11M4.5 13.5L7 11M11.5 13.5V2.5M11.5 2.5L9 5M11.5 2.5L14 5"
                  stroke="currentColor"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>

          <AssetAmountField
            amount={receiveAmount}
            label={baseLabel ? (isSellingSide ? "You receive" : `Buy ${baseLabel}`) : "You receive"}
            token={buyDisplay}
            tokens={buyTokenList}
            usedAssetIds={buyUsed}
            onSelectToken={handleSelectBuy}
            readOnly
            helperText={undefined}
            amountClassName="text-[26px]"
            className="rounded-[28px] bg-[#170A10]"
          />
        </div>

        <div className="flex items-center gap-2 rounded-[24px] bg-sr-espresso px-3 py-3">
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            className="text-sr-neutral-500 shrink-0"
          >
            <path
              d="M2 7H12M2 7L4 5M2 7L4 9M12 7L10 5M12 7L10 9"
              stroke="currentColor"
              strokeWidth="1.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span className="text-[11px] text-sr-neutral-500">
            {matched ? formatMarketPairLabel(matched.market) : "Select a pair"}
          </span>
          <div className="flex-1" />
          <div className="flex items-center gap-3">
            {matched && (
              <span className="text-[10px] text-sr-neutral-300">
                Fee: {Number(matched.market.takerFee.numerator) / Number(matched.market.takerFee.denominator) * 100}%
              </span>
            )}
          </div>
        </div>
        {avgFillPrice && (
          <div className="flex items-center justify-between rounded-[24px] bg-sr-espresso px-3 py-3">
            <span className="text-[11px] text-sr-neutral-500">Average Price</span>
            <span className="text-sm font-medium text-white tabular-nums font-display">
              {avgFillPrice}
            </span>
          </div>
        )}
      </div>

      {/* Place Order button */}
      <button
        className="w-full py-3 bg-sr-lemon-muted text-sr-espresso font-display font-medium text-lg rounded-xl hover:bg-sr-lemon transition-colors tracking-tight disabled:opacity-50 disabled:cursor-not-allowed"
        disabled={
          !matched ||
          numericAmount <= 0 ||
          isPlacingOrder ||
          !myAccount ||
          (effectiveOrderType === "limit" && !limitPrice.trim())
        }
        onClick={handlePlaceOrder}
      >
        {isPlacingOrder
          ? "Placing Order..."
          : !myAccount
            ? "Deposit to Trade"
            : `${isSellingSide ? "Sell" : "Buy"}${baseLabel ? ` ${baseLabel}` : ""} — ${effectiveOrderType === "limit" ? "Limit" : "Market"}`}
      </button>
    </div>
  );
};
